import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  signal,
  ViewChild
} from '@angular/core';
import {
  ActivationEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';

import { NgModel } from '@angular/forms';
import { Observable, throwError } from 'rxjs';
import { catchError, delay, first, tap } from 'rxjs/operators';
import { RestrictIfService } from 'src/app/auth-o3/shared/restrict/restrict-if.service';
import { AuthService } from 'src/app/auth-o3/shared/services/auth.service';
import { UserPreferencesService } from 'src/app/auth-o3/shared/services/user-preferences.service';
import { AppSettingsService, GlobalSettings } from 'src/app/global-settings/global-settings.service';
import { NAVBAR_ROUTES } from './navbar.consts';

@Component({
  selector: 'app-navbar',
  templateUrl: './dc-navbar.component.html',
  styleUrls: ['./dc-navbar.component.scss'],
})
export class DcNavbarComponent implements OnInit {
  @Input() user: string;
  @Input() email: string;
  @Input() loggedInAsDistributor: boolean;
  @Input() navigating: boolean;
  @Input() isAuthenticated: boolean;
  @Output() signOutEmitter: EventEmitter<any> = new EventEmitter();
  @Output() loadedEmitter: EventEmitter<any> = new EventEmitter();
  @ViewChild('userMenu') userMenu: ElementRef;
  @ViewChild('toggleButton') toggleButton: ElementRef;

  navItems = NAVBAR_ROUTES;

  settings = [
    { 
      label: 'Emulate DB Environment', 
      key: 'emulateDBEnviroment', 
      visible: computed(() => this.hostname().includes('test.sandbox') || this.hostname().includes('localhost')),
      value: computed(() => this.userPreferences()?.userSettings?.emulateDBEnviroment), 
    }
  ];

  hasVisibleSettings = computed(() =>
    this.settings.some(setting => setting.visible())
  );

  hostname = signal<string>(location.hostname);

  userPreferences = signal(null);

  loadingPreferences = false;

  public activeRoute: string;
  public userPanelCollapsed: boolean;
  public navbarMenuCollapsed: boolean;

  environment$: Observable<string>;
  loading$: Observable<boolean>;
  private readonly appSettingsService = inject(AppSettingsService);

  constructor(
    router: Router,
    private renderer: Renderer2,
    private authService: AuthService,
    private restricIfService: RestrictIfService,
    private userPreferencesService: UserPreferencesService
  ) {
    router.events.subscribe((event) => {
      if (event instanceof RouterEvent) {
        this.navigationInterceptor(event);
      }
    });
    this.renderer.listen('window', 'click', (e: Event) => {
      if (
        !this.userMenu?.nativeElement?.contains(e.target) &&
        !this.toggleButton?.nativeElement?.contains(e.target)
      ) {
        this.userPanelCollapsed = true;
      }
    });

    this.environment$ = this.authService.getEnvironmentName();
    this.loading$ = this.restricIfService.permissionsLoaded().pipe(
      delay(200),
      tap(async (loaded) => {
        await this.appSettingsService.initialize();
        loaded && this.loadedEmitter.emit()
      })
    );
  }

  ngOnInit(): void {
    this.navbarMenuCollapsed = true;
    this.userPanelCollapsed = true;
  }

  toggleUserPanel(): void {
    this.userPanelCollapsed = !this.userPanelCollapsed;
    this.navbarMenuCollapsed = true;

    if (!this.userPreferences()) {
      this.userPreferences.set(GlobalSettings.getAppSettings().userPreferences);
    }

  }

  loadPreferences(): void {
    this.loadingPreferences = true;
    this.userPreferencesService
      .getPreferences()
      .pipe(
        catchError(err => {
          this.loadingPreferences = false;
          return throwError(err);
        }),
        delay(300)
      )
      .subscribe((preferences) => {
        this.userPreferences.set(preferences);

        GlobalSettings.setAppSettings({
          userPreferences: preferences,
        });
        
        this.loadingPreferences = false;
      });
  }

  navbarMenuButton(): void {
    this.navbarMenuCollapsed = !this.navbarMenuCollapsed;
    this.userPanelCollapsed = true;
  }

  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.navigating = true;
      this.activeRoute = undefined;
      this.navbarMenuCollapsed = true;
      this.userPanelCollapsed = true;
    }
    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      this.navigating = false;
      this.navbarMenuCollapsed = true;
      this.userPanelCollapsed = true;
    }
    if (event instanceof ActivationEnd) {
      this.activeRoute = event.snapshot.routeConfig.path;
      this.navbarMenuCollapsed = true;
      this.userPanelCollapsed = true;
    }
  }

  onSettingChange(setting: string, value: boolean, model: NgModel): void {
    if (!model.touched) {
      return;
    }

    this.userPreferencesService
      .saveOrUpdate({ userSettings: { [setting]: value } })
      .pipe(
        first(),
        delay(300)
      )
      .subscribe(() => {
        this.loadPreferences();
      });
  }

  signOut(): void {
    this.signOutEmitter.emit();
  }
}
